import { useState, memo } from 'react'
import { STATUS_COLORS, STATUS_LABELS } from '../hooks/useWatchlist'
import { HeatBar } from './HeatBar'
import { useNavigate } from 'react-router-dom'
import { FadeImg } from './FadeImg'
import { ShowStatusSheet } from './ShowStatusSheet'
import { ConfirmSheet } from './ConfirmSheet'
import { StatusRibbon } from './StatusRibbon'
import { revivalInfo } from './RevivalBanner'
import { Plus, Check, MoreHorizontal } from 'lucide-react'
import { posterFor } from '../lib/tmdb'
import { progressPct, effectiveLastAired, isBehind } from '../lib/episode'
import { nextMark } from '../lib/episodeProgress'
import { todayKey } from '../lib/datetime'
import { Clapboard } from './ClapboardIcon'
import { ReelDisplay } from './ReelRating'
import { useFriendsOnShow } from '../hooks/useFriendsWatching'
import { FriendBubblesCompact } from './FriendBubbles'
import { navigateToShow } from '../lib/viewTransition'
import { MatchBadge } from './MatchBadge'
import { useThirdPartyRatings } from '../hooks/useThirdPartyRatings'

// memo: rendered in bulk across Home/Discover/MyList grids. Now that `wl` has a stable
// identity (useWatchlist memoizes its return), memoizing here actually pays off — cards
// stop re-rendering on unrelated parent renders. Props are primitives + the stable `wl`
// (+ raceShowIds, a stable Set/array from App), so default shallow compare is correct.
export const ShowCard = memo(function ShowCard({ show, wl, compact = false, raceShowIds, gridMark = false }) {
  const navigate      = useNavigate()
  const [popping,     setPopping]     = useState(false)
  const [burst,       setBurst]       = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [justMarked,  setJustMarked]  = useState(false)
  const { friends }   = useFriendsOnShow(wl?.userId, show.id)
  const inList = wl.has(show.id)
  // Passive: nearly every ShowCard in the app renders compact (Discover, Search,
  // ForYou, Person, rails) — gating this to full cards only would mean it almost
  // never shows. Safe either way because `passive` only ever reads the existing
  // cache (one indexed select), never triggers a new OMDb fetch — the show-detail
  // page is what actually populates a row on a miss, so a 30-card grid can't turn
  // into a request storm no matter how many cards are on screen at once.
  const { imdb: imdbRating } = useThirdPartyRatings(show.id, { passive: true })

  // Grid quick-mark (opt-in via gridMark — only My List's grid, not Discover/Person):
  // parity with the list row so the mark-watched core loop doesn't depend on view mode.
  const gmProg   = wl.progress?.[show.id]
  const canMark  = gridMark && inList && wl.statuses?.[show.id] === 'watching' && !!gmProg &&
    isBehind(gmProg, effectiveLastAired(show, todayKey()))
  const markNow  = (e) => {
    e.stopPropagation()
    if (!gmProg || !wl.saveProgress) return
    const { prog, opts } = nextMark(show, { season: gmProg.season, episode: gmProg.episode })
    wl.saveProgress(show.id, prog, opts)
    setBurst(true); setTimeout(() => setBurst(false), 550)
    setJustMarked(true); setTimeout(() => setJustMarked(false), 1600)
  }
  const name   = show.name || show.original_name || ''
  const rating = wl.ratings[show.id]
  const w      = compact ? 110 : 140
  // Sized off `w` and the real DPR (see posterFor). The old hard-coded w185 assumed
  // ~1.3× DPR, which no phone is — every rail rendered upscaled.
  const poster = posterFor(show.poster_path, w)

  const [confirmRemove, setConfirmRemove] = useState(false)
  const handleToggle = async (e) => {
    e.stopPropagation()
    setPopping(true); setTimeout(() => setPopping(false), 200)
    if (!inList) {
      setBurst(true); setTimeout(() => setBurst(false), 550)
      await wl.add(show)
    } else {
      // Show remove confirmation in action sheet
      setShowActions(true)
    }
  }

  return (
    <>
      {showActions && <ShowStatusSheet show={show} wl={wl} mode="status" onClose={() => setShowActions(false)} onRemove={() => setConfirmRemove(true)} />}
      <ConfirmSheet open={confirmRemove} emoji="🗑️" title={`Remove ${name}?`} body="It leaves your list. Your progress and rating are kept if you add it back." confirmLabel="Remove" onCancel={() => setConfirmRemove(false)} onConfirm={() => { wl.remove(show.id); setConfirmRemove(false) }} />

      <div className="tap-card" style={{
        position:'relative', borderRadius:'var(--radius)',
        background:'var(--bg-raised)', flexShrink:0, width:w,
        userSelect:'none', WebkitUserSelect:'none', WebkitTouchCallout:'none',
        boxShadow: inList ? '0 0 0 2px rgba(var(--amber-rgb),0.7)' : undefined,
      }}>
        {/* Was clickable() on this whole card, wrapping the Add/Remove, ⋯,
            grid-mark and friend-avatar <button>s inside it — a nested-interactive
            axe violation (role="button" containing real buttons), and per the ARIA
            content model for role=button, AT is free to flatten those nested
            buttons into the card's accessible name instead of exposing them at all
            (2026-09-17 accessibility follow-up). Now a real, transparent, full-cover
            <button> UNDER the content instead: it's a sibling of the action
            buttons, not their ancestor, so each stays an independently reachable
            control. The action buttons already sit at position:absolute with
            zIndex 2-4; this cover is zIndex 0, so normal stacking order (later
            siblings win ties) puts it behind everything without needing to touch
            their z-indexes.
            Their existing e.stopPropagation() calls are now dead — nothing to
            propagate to — but harmless, so left in place rather than risk missing
            one during this pass. */}
        <button onClick={(e) => navigateToShow(navigate, show.id, e)} aria-label={name || 'Open show'} style={{
          position:'absolute', inset:0, zIndex:0, width:'100%', height:'100%',
          background:'none', border:'none', padding:0, margin:0, cursor:'pointer',
          borderRadius:'var(--radius)',
        }} />
        {/* Image */}
        <div style={{ borderRadius:'var(--radius)', overflow:'hidden', width:'100%', aspectRatio:'2/3', background:'var(--surface)' }}>
          {poster
            ? <FadeImg src={poster} alt={name}
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', WebkitTouchCallout:'none', pointerEvents:'none' }} />
            : <div style={{ width:'100%', height:'100%', background:'var(--surface)', display:'flex', alignItems:'center', justifyContent:'center', padding:8, fontFamily:'var(--font-serif)', fontSize:13, color:'var(--text-dim)', textAlign:'center', lineHeight:1.2 }}>{name}</div>
          }
          {!compact && (
            <div style={{
              position:'absolute', bottom:0, left:0, right:0,
              background:'linear-gradient(transparent, rgba(var(--bg-rgb),0.95))',
              padding:'24px 8px 8px', borderRadius:'0 0 12px 12px',
            }}>
              <div style={{ fontSize:11, fontWeight:500, color:'var(--text)', lineHeight:1.3,
                overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                {name}
              </div>
              {(rating > 0 || imdbRating) && (
                <div style={{ marginTop:3, display:'flex', alignItems:'center', gap:7 }}>
                  {rating > 0 && <ReelDisplay value={rating} size={10}/>}
                  {imdbRating && (
                    <span style={{ display:'flex', alignItems:'baseline', gap:3, fontSize:9.5 }}>
                      <span style={{ color:'var(--amber)', fontWeight:700, letterSpacing:'0.02em' }}>IMDb</span>
                      <span style={{ color:'var(--text-dim)', fontWeight:600 }}>{imdbRating}</span>
                    </span>
                  )}
                </div>
              )}
              {/* Friends watching, moved off the poster (2026-09-17): it used to sit
                  absolute bottom-left on top of THIS title, which a new user reads
                  as a stray face covering the show name rather than a "friends
                  watching too" signal. Now a real inline row under the title —
                  each avatar links to that friend's profile, with a desktop hover
                  card (name, shows in common, follow). */}
              {friends.length > 0 && (
                <div style={{ marginTop:5 }}>
                  <FriendBubblesCompact friends={friends} size={18} wl={wl} userId={wl?.userId} />
                </div>
              )}
            </div>
          )}
          {/* Race badge, offset right of the ⋯ quick-actions button (both lived at
              top:7,left:7; the badge covered the button and blocked its tap, audit
              2026-07-08). Other corners are owned: ribbon TR, add BR, bubbles BL. */}
          {/* 17: the Racing badge is gone with the race doors. */}
                    <StatusRibbon show={show} status={wl?.statuses?.[show.id]} wl={wl} />
          {/* Reel Match %, top-left — only on NOT-in-list cards (the ⋯ button and
              status dot own that corner once the show is added, and a match score
              is only a useful question for shows you haven't committed to). */}
          {!inList && <MatchBadge show={show} wl={wl} />}
        </div>

        {compact && (
          <div style={{ padding:'5px 7px' }}>
            <div style={{ fontSize:11, fontWeight:500, color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</div>
            {/* Status label, replaces year so users always know where they stand.
                Same revival check as StatusRibbon — a completed show with a new season
                out shouldn't keep captioning itself "Completed" here either. */}
            {inList && wl.statuses?.[show.id] ? (
              revivalInfo(show, wl.statuses[show.id], wl.completedAt?.[show.id], wl.progress?.[show.id]) ? (
                <div style={{ fontFamily:'var(--font-mono)', fontSize:9.5, marginTop:3, color:'var(--amber)', textTransform:'uppercase', letterSpacing:'0.1em' }}>
                  New season
                </div>
              ) : (
                <div style={{ fontFamily:'var(--font-mono)', fontSize:9.5, marginTop:3, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.1em', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {STATUS_LABELS[wl.statuses[show.id]] || ''}
                </div>
              )
            ) : (
              (show.first_air_date || imdbRating) && (
                <div style={{ fontSize:10, color:'var(--text-dim)', display:'flex', alignItems:'baseline', gap:5, overflow:'hidden' }}>
                  {show.first_air_date && <span>{show.first_air_date.slice(0,4)}</span>}
                  {imdbRating && (
                    <span style={{ whiteSpace:'nowrap' }}>
                      <span style={{ color:'var(--amber)', fontWeight:700 }}>IMDb</span> {imdbRating}
                    </span>
                  )}
                </div>
              )
            )}
            {friends.length > 0 && (
              <div style={{ marginTop:4 }}>
                <FriendBubblesCompact friends={friends} size={16} wl={wl} userId={wl?.userId} />
              </div>
            )}
          </div>
        )}

        {/* Add / Remove button, BOTTOM-right of the poster. The status ribbon
            owns the top-right corner; stacking a round button on the diagonal
            band made both look broken (Jason screenshots 2026-06-11). */}
        <button onClick={handleToggle} className="ui-hit" aria-label={inList ? `Remove ${name} from your list` : `Add ${name} to your list`} style={{
          position:'absolute', right:7, bottom: compact ? 38 : 44,
          background: inList ? 'var(--amber)' : 'rgba(var(--bg-rgb),0.78)',
          border: inList ? 'none' : '1px solid var(--border)',
          borderRadius:'var(--radius-full)', width:30, height:30,
          display:'flex', alignItems:'center', justifyContent:'center',
          cursor:'pointer', zIndex:4,
          backdropFilter:'blur(4px)',
          transform: popping ? 'scale(1.4)' : 'scale(1)',
          transition:'transform 150ms var(--ease-pop), background 150ms',
        }}>
          {inList ? <Check size={13} color="var(--amber-ink)" strokeWidth={3}/> : <Plus size={13} color="var(--text)"/>}
        </button>

        {/* ⋯ quick actions button, only on in-list cards */}
        {inList && (
          <button onClick={e => { e.stopPropagation(); setShowActions(true) }} className="ui-hit" aria-label={`More actions for ${name}`} style={{
            position:'absolute', top:7, left:7,
            background:'rgba(var(--bg-rgb),0.75)', border:'1px solid var(--border)',
            borderRadius:'var(--radius-full)', width:28, height:28,
            display:'flex', alignItems:'center', justifyContent:'center',
            cursor:'pointer', zIndex:2,
          }}>
            <MoreHorizontal size={13} color="var(--text)"/>
          </button>
        )}

        {/* Grid quick-mark, clapboard, opposite corner from More (opt-in via gridMark) */}
        {canMark && (
          <button onClick={markNow} aria-label={`Mark S${gmProg.season}·E${gmProg.episode} watched`} title="Mark next episode watched" style={{
            position:'absolute', top:7, right:7,
            background: 'rgba(var(--bg-rgb),0.78)',
            border: justMarked ? 'none' : '1px solid var(--border)',
            borderRadius:'var(--radius-full)', width:28, height:28,
            display:'flex', alignItems:'center', justifyContent:'center',
            cursor:'pointer', zIndex:2, transition:'all 0.25s var(--ease-spring)', transform: justMarked ? 'scale(1.12)' : 'scale(1)',
          }}>
            <Clapboard size={16} watched={justMarked} accent="var(--amber)" idle="var(--text)"/>
          </button>
        )}

        {/* Rewatch indicator: mono tag, no emoji */}
        {wl?.statuses?.[show.id] === 'rewatching' && (
          <div style={{ position:'absolute', top:39, left:7, background:'rgba(var(--bg-rgb),0.78)', border:'1px solid var(--border)', borderRadius:6, padding:'2px 6px', fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.1em', color:'var(--amber)', zIndex:2 }}>
            REWATCH{wl?.rewatchCount?.[show.id] > 1 ? ` ×${wl.rewatchCount[show.id]}` : ''}
          </div>
        )}
        {/* Season progress bar, watching shows only */}
        {inList && wl?.statuses?.[show.id] === 'watching' && (() => {
          const prog = wl.progress?.[show.id]
          if (!prog?.season || !show.seasons) return null
          // Series-scope %, next-to-watch aware (shared helper, 2026-06-29).
          const pct = progressPct(prog, show.seasons, 'watching', 'series')
          if (pct <= 0) return null
          return (
            <>
              {/* Where-am-I label, answers "which episode?" at a glance in the grid. */}
              <span style={{ position:'absolute', bottom:6, left:5, background:'rgba(var(--bg-rgb),0.82)', color:'var(--text)', fontSize:8.5, fontWeight:700, padding:'1px 5px', borderRadius:4, zIndex:3, letterSpacing:'0.02em' }}>S{prog.season}·E{prog.episode}</span>
              <div style={{
                position:'absolute', bottom:0, left:0, right:0, zIndex:3,
              }}>
                <HeatBar frac={pct / 100} height={3} track="var(--surface)" flame={false} />
              </div>
            </>
          )
        })()}

        {/* Status indicator dot - color-coded by current status */}
        {inList && wl.statuses?.[show.id] && (
          <div style={{
            position:'absolute', top:9, left:39, width:10, height:10, borderRadius:'var(--radius-full)',
            background: STATUS_COLORS[wl.statuses[show.id]] || 'rgba(var(--ink-rgb),0.4)',
            border:'1.5px solid rgba(var(--bg-rgb),0.8)', zIndex:2,
            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
          }} title={STATUS_LABELS[wl.statuses[show.id]] || wl.statuses[show.id]}/>
        )}

        {/* Burst ring */}
        {burst && (
          <div style={{
            position:'absolute', bottom: compact ? 31 : 37, right:0, width:44, height:44,
            borderRadius:'var(--radius-full)', border:'2px solid var(--amber)',
            '--nxa':'burstRing 550ms ease-out forwards',
            pointerEvents:'none', zIndex:5,
          }}/>
        )}
      </div>
    </>
  )
})
